/**
 * BranchConditionBadge - Colored badge for branch conditions
 */

import React from "react";
import clsx from "clsx";
import type { BranchCondition } from "../../types/sequence.types";

interface BranchConditionBadgeProps {
  condition: BranchCondition;
  config?: Record<string, unknown>;
  className?: string;
}

const CONDITION_STYLES: Record<
  BranchCondition,
  { bg: string; text: string; label: string }
> = {
  default: {
    bg: "bg-gray-100",
    text: "text-gray-700",
    label: "Default",
  },
  email_opened: {
    bg: "bg-green-100",
    text: "text-green-700",
    label: "Email Opened",
  },
  email_not_opened: {
    bg: "bg-red-100",
    text: "text-red-700",
    label: "Not Opened",
  },
  link_clicked: {
    bg: "bg-blue-100",
    text: "text-blue-700",
    label: "Link Clicked",
  },
  link_not_clicked: {
    bg: "bg-orange-100",
    text: "text-orange-700",
    label: "Not Clicked",
  },
  form_submitted: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    label: "Form Submitted",
  },
  tag_applied: {
    bg: "bg-teal-100",
    text: "text-teal-700",
    label: "Tag Applied",
  },
  tag_not_applied: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    label: "Tag Not Applied",
  },
  contact_field_equals: {
    bg: "bg-indigo-100",
    text: "text-indigo-700",
    label: "Field Equals",
  },
  contact_field_not_equals: {
    bg: "bg-pink-100",
    text: "text-pink-700",
    label: "Field Not Equals",
  },
  in_segment: {
    bg: "bg-cyan-100",
    text: "text-cyan-700",
    label: "In Segment",
  },
  not_in_segment: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    label: "Not In Segment",
  },
};

const BranchConditionBadge: React.FC<BranchConditionBadgeProps> = ({
  condition,
  config,
  className,
}) => {
  const style = CONDITION_STYLES[condition] || CONDITION_STYLES.default;

  let label = style.label;
  if (config) {
    if (
      (condition === "tag_applied" || condition === "tag_not_applied") &&
      config.tag
    ) {
      label += `: ${config.tag}`;
    } else if (
      (condition === "contact_field_equals" ||
        condition === "contact_field_not_equals") &&
      config.field
    ) {
      label += `: ${config.field} = ${config.value}`;
    } else if (
      (condition === "in_segment" || condition === "not_in_segment") &&
      config.segment_id
    ) {
      label += `: ${(config.segment_id as string).substring(0, 8)}...`;
    }
  }

  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
        style.bg,
        style.text,
        className,
      )}
    >
      {label}
    </span>
  );
};

export default BranchConditionBadge;
